import { forwardRef, useImperativeHandle, useState } from "react";
import {
  Button,
  Group,
  Loader,
  Popover,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { appShowNotification } from "@/utils/utils";
import { createTemplate } from "@/graphql/mutations";
import { databaseMigrationAdapter } from "@/services/DatabaseMigrationAdapter";
import {
  CreateTemplateInput,
  CreateTemplateMutation,
  CreateTemplateMutationVariables,
} from "@/API";

const client = generateClient();

interface NewTemplateFormProps {
  onNextStep: (template: any) => void;
  fetchTemplates?: () => void;
  mixpanel?: any;
}

const NewTemplateForm = forwardRef<unknown, NewTemplateFormProps>(
  (props, ref) => {
    const { onNextStep, fetchTemplates, mixpanel } = props;
    const [templateName, setTemplateName] = useState("");
    const [showAdvanceTooltip, setShowAdvanceTooltip] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isAdvancedTemplate, setIsAdvancedTemplate] = useState(false);

    const handleNext = async () => {
      await handleSaveTemplate();
    };

    const handleSaveTemplate = async () => {
      if (!templateName.trim()) {
        appShowNotification({
          message: "Please provide a name",
          title: "Warning",
          variant: "warning",
        });
        return;
      }
      setLoading(true);
      try {
        const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
        const createTemplateInput: CreateTemplateInput = {
          name: templateName,
          lID: userInfo["custom:location"]!,
          advanced: isAdvancedTemplate,
        };
        const createTemplateVars: CreateTemplateMutationVariables = {
          input: createTemplateInput,
        };
        mixpanel?.track?.("Create Template", {});
        console.log('[REACT-HOOK] Calling Amplify createTemplate GraphQL mutation');
        const createTemplateResult = await client.graphql<
          GraphQLQuery<CreateTemplateMutation>
        >({
          query: createTemplate,
          variables: createTemplateVars,
        });
        
        console.log('[REACT-HOOK] Amplify template creation successful, calling database migration service');
        // Call database migration service for logging and dual-write
        await databaseMigrationAdapter.handleTemplateOperation('CREATE', createTemplateInput, createTemplateResult as unknown as Record<string, unknown>);
        
        fetchTemplates?.();
        onNextStep(createTemplateResult?.data?.createTemplate);
        return createTemplateResult?.data?.createTemplate;
      } catch (err) {
        console.error("error creating template:", err);
        return null;
      } finally {
        setLoading(false);
      }
    };

    const resetForm = () => {
      setTemplateName("");
      setIsAdvancedTemplate(false);
    };

    useImperativeHandle(ref, () => ({
      resetForm,
    }));

    return (
      <Stack>
        <Text size="sm">
          Input the following information to prepare your template.
        </Text>
        <TextInput
          label="Template Name"
          placeholder="Example: Annual Check-Up"
          value={templateName}
          onChange={(event) => setTemplateName(event.currentTarget.value)}
          required
        />
        <Group gap={0} mt="md">
          <Text size="sm" fw={500}>
            Advanced Template
          </Text>
          <Popover
            opened={showAdvanceTooltip}
            onClose={() => setShowAdvanceTooltip(false)}
            width={260}
            position="top"
            withArrow
            offset={3}
          >
            <Popover.Target>
              <Button
                variant="subtle"
                size="xs"
                pl={3}
                pr={3}
                mr={10}
                onClick={() => setShowAdvanceTooltip(!showAdvanceTooltip)}
              >
                <IconInfoCircle size={20} />
              </Button>
            </Popover.Target>
            <Popover.Dropdown bg="#BAD7D2" style={{ border: "none" }}>
              <Stack gap={0}>
                <Text fw={600} size="sm">
                  Advanced Templates:
                </Text>
                <Text size="sm">
                  Ideal for detailed records with multiple sections (e.g., full
                  physical exams).
                </Text>
                <Text fw={600} size="sm" mt={20}>
                  Standard Templates:
                </Text>
                <Text size="sm">
                  Best for simpler notes with basic sections (e.g., quick
                  progress checks).
                </Text>
                <Button
                  variant="white"
                  size="xs"
                  w={100}
                  ml="auto"
                  mt={20}
                  onClick={() => setShowAdvanceTooltip(false)}
                >
                  Done
                </Button>
              </Stack>
            </Popover.Dropdown>
          </Popover>
          <Switch
            checked={isAdvancedTemplate}
            onChange={(event) =>
              setIsAdvancedTemplate(event.currentTarget.checked)
            }
          />
        </Group>
        <Button
          fullWidth
          mt="xl"
          radius={30}
          h={40}
          fw={500}
          onClick={handleNext}
          disabled={
            loading || (templateName.trim() === "" && !isAdvancedTemplate)
          }
        >
          {loading ? (
            <Loader size="sm" />
          ) : isAdvancedTemplate ? (
            "Save"
          ) : (
            "Next"
          )}
        </Button>
      </Stack>
    );
  },
);

export default NewTemplateForm;
